import api from '../api/axios';
import {
  saveMedicineResults, saveInventoryRows, saveCustomers, saveSuppliers, saveCategories,
  saveSettingsCache, saveSales, saveSaleReturns, savePurchases, getQueue, removeQueue,
  updateQueueItem, setMeta, getMeta, STORES, put, remove, removeMedicineCacheByMedicineId,
  hasPendingQueue, replaceInventoryRows, pruneMedicineBatchRows, get, getAll,
} from './db';
import { warmGenericCaches, removeOfflineRowFromCaches } from './generic';

// In-flight guards. A second caller gets the SAME promise back instead of an
// instant "nothing to do" — otherwise the manual "Sync" button (and anything
// chained after a sync) reported success while the real sync was still running.
let pullPromise = null;
let pushPromise = null;
let syncPromise = null;
const listeners = new Set();

export function onSyncEvent(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function emit(evt) { listeners.forEach(fn => { try { fn(evt); } catch { /* noop */ } }); }

const unwrap = (res) => res?.data?.data ?? res?.data ?? [];

// The default 8s axios timeout is meant for interactive screens. A bulk pull
// (the server loads and populates the whole list for every page) or a heavier
// write can legitimately take longer; with 8s they failed silently, every time,
// so the caches were never refreshed and the queued item never went through.
const SYNC_TIMEOUT = 60000;
const isTimeout = (error) => !error?.response && (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT' || /timeout/i.test(error?.message || ''));

async function fetchAllWithTotal(endpoint, params = {}) {
  const limit = 500;
  const first = await api.get(endpoint, { params: { ...params, page: 1, limit }, timeout: SYNC_TIMEOUT });
  const firstRows = unwrap(first);
  const total = Number(first?.data?.pagination?.total || firstRows.length);
  const pages = Math.min(100, Math.max(1, Math.ceil(total / limit)));
  if (pages === 1) return { rows: firstRows, total };
  const rows = [...firstRows];
  for (let page = 2; page <= pages; page++) {
    const res = await api.get(endpoint, { params: { ...params, page, limit }, timeout: SYNC_TIMEOUT });
    const part = unwrap(res);
    rows.push(...part);
    if (part.length < limit) break;
  }
  return { rows, total };
}

async function fetchAll(endpoint, params = {}) {
  return (await fetchAllWithTotal(endpoint, params)).rows;
}

// Pulls down everything the offline-capable pages (medicines, inventory,
// purchases/sales, dashboard, accounting) need to work fully without a
// network connection, and stores it in IndexedDB.
//
// This is a PULL: it overwrites the local cache with what the server has. If
// there are still offline changes waiting in the queue the server doesn't
// know about them yet, so pulling now would wipe them off the screen (stock
// would jump back to the old number until the queue finally went through).
// In that case it does nothing — syncQueue() pushes first and pulls after.
export function syncReferenceData({ silent = true } = {}) {
  if (!navigator.onLine) return Promise.resolve();
  if (pullPromise) return pullPromise;
  pullPromise = doSyncReferenceData({ silent }).finally(() => { pullPromise = null; });
  return pullPromise;
}

async function doSyncReferenceData({ silent }) {
  if (await hasPendingQueue()) return;
  emit({ type: 'sync-start' });
  try {
    const jobs = [
      fetchAll('/medicines', { isActive: true }).then(rows => saveMedicineResults(rows)).catch(() => null),
      fetchAllWithTotal('/inventory').then(async ({ rows, total }) => {
        await saveMedicineResults(rows);
        // Only when we really got the COMPLETE list and nothing was queued in
        // the meantime may the local copy be replaced outright (this is what
        // removes ghost/duplicate rows). Otherwise just add/update.
        if (rows.length >= total && !(await hasPendingQueue())) {
          await replaceInventoryRows(rows);
          await pruneMedicineBatchRows(rows);
        } else {
          await saveInventoryRows(rows);
        }
      }).catch(() => null),
      fetchAll('/customers').then(rows => saveCustomers(rows)).catch(() => null),
      fetchAll('/suppliers').then(rows => saveSuppliers(rows)).catch(() => null),
      fetchAll('/categories', { limit: 100 }).then(rows => saveCategories(rows)).catch(() => null),
      // Purchases / Sales / Sale-Returns were never actually pulled down here
      // (despite the comment above claiming they were) — that's why those
      // pages showed nothing while offline unless something had been
      // created offline in that exact session. Now they're cached like
      // everything else above.
      fetchAll('/sales').then(rows => saveSales(rows)).catch(() => null),
      fetchAll('/sale-returns').then(rows => saveSaleReturns(rows)).catch(() => null),
      fetchAll('/purchases').then(rows => savePurchases(rows)).catch(() => null),
      warmGenericCaches(fetchAllWithTotal).catch(() => null),
      api.get('/settings').then(res => saveSettingsCache(res.data?.settings || res.data?.data || {})).catch(() => null),
    ];
    await Promise.all(jobs);
    await setMeta('lastReferenceSync', Date.now());
  } finally {
    emit({ type: 'sync-end' });
  }
  return !silent;
}

// Which local store a successful "create" response should replace the
// temporary offline record in (so the UI never shows a stale offline copy
// side-by-side with the real synced one).
const CREATE_STORE_BY_URL = {
  '/customers': STORES.customers,
  '/suppliers': STORES.suppliers,
  '/sales': STORES.sales,
  '/sale-returns': STORES.saleReturns,
  '/purchases': STORES.purchases,
  '/accounting/expenses': STORES.expenses,
};

// Replaces every occurrence of a known offline id with its real server id,
// inside a URL string or anywhere in a JSON-serialisable payload. This is
// what lets e.g. a sale created offline (referencing an offline customer id)
// sync correctly once that customer has been created for real.
function remap(value, idMap) {
  if (!value || !Object.keys(idMap).length) return value;
  let str = typeof value === 'string' ? value : JSON.stringify(value);
  for (const [offlineId, realId] of Object.entries(idMap)) {
    if (str.includes(offlineId)) str = str.split(offlineId).join(realId);
  }
  return typeof value === 'string' ? str : JSON.parse(str);
}

// An inventory row created offline by a purchase "receive" (or a sale-return
// restock) has a temporary "offline-inv-…" id, but — unlike a manual
// Stock In — the request that creates the real row on the server carries no
// id to map it by. So an edit made to that row offline (e.g. setting its
// selling price) was queued against an id the server has never heard of and
// failed with 404 forever. Once the creating request has gone through, find
// the real row by medicine + batch.
const OFFLINE_INV_URL = /\/inventory\/summary\/(offline-inv-[^/?]+)/;

async function resolveOfflineInventoryId(offlineId) {
  const local = await get(STORES.inventory, offlineId);
  if (!local) return null;
  const batch = String(local.origBatchNumber ?? local.batchNumber ?? '');
  const { rows } = await fetchAllWithTotal('/inventory');
  const hit = rows.find(r =>
    String(r.medicine?._id || r.medicine) === String(local.medicineId) && String(r.batchNumber || '') === batch
  );
  return hit?._id ? String(hit._id) : null;
}

// Push first, then pull — always in that order and never at the same time.
// (They used to be started in parallel on login/reconnect, so the pull could
// download the OLD server numbers and overwrite the local cache while the
// queued stock changes were still being sent, and the follow-up pull was then
// skipped because one was "already running".)
export function syncQueue() {
  if (!navigator.onLine) return Promise.resolve();
  if (syncPromise) return syncPromise;
  syncPromise = (async () => {
    await pushQueue();
    await syncReferenceData();
  })().finally(() => { syncPromise = null; });
  return syncPromise;
}

function pushQueue() {
  if (pushPromise) return pushPromise;
  pushPromise = doPushQueue().finally(() => { pushPromise = null; });
  return pushPromise;
}

// Pushes queued offline writes (create/update/delete for medicines,
// inventory, purchases, sales, expenses, ledger payments, ...) to the
// server, in the order they were made.
//
// - A network failure stops the run (nothing after it could reach the
//   server either); the whole queue is retried on the next reconnect.
// - A real 4xx/5xx from the server is left in the queue (so nothing is
//   silently lost) but does NOT stop the rest of the queue from syncing —
//   one bad item must never block every other pending sale/purchase forever.
// - IDs generated while offline (customers, suppliers, sales, purchases,
//   expenses, medicines) are remapped to the real server IDs as soon as
//   they're known, both in the remaining queue and in the local cache.
// ── offline id → real id, remembered across sync runs ──────────────────────
// A change made on a record that only existed offline (approve a purchase,
// return a sale, ...) is queued with the record's temporary "offline-…" id.
// That id is swapped for the real one only if the request that CREATES the
// record went through in the SAME run. If the connection dropped (or the
// request timed out) between the create and the follow-up, the next run no
// longer knew the mapping and sent the temporary id to the server, which
// answered "Cast to ObjectId failed for value offline-…" — for every such
// change, forever. The map is now kept in IndexedDB, and if it is missing an
// id it is rebuilt from the server's own `clientOfflineId` field.
const OFFLINE_ID_RE = /offline-[a-z]+-\d{10,}-[a-z0-9]+/g;
const SERVER_LIST_BY_PREFIX = { sale: '/sales', pur: '/purchases', cust: '/customers' };
const RECOVER_STORES = [STORES.sales, STORES.purchases, STORES.customers, STORES.suppliers, STORES.expenses, STORES.saleReturns];

async function loadIdMap() { return { ...((await getMeta('idMap')) || {}) }; }
async function persistIdMap(map) {
  const keys = Object.keys(map);
  const kept = keys.length > 5000 ? Object.fromEntries(keys.slice(-5000).map(k => [k, map[k]])) : map;
  await setMeta('idMap', kept);
}

// Real id of a record created offline, found from what the server told us.
// Throws only for a dropped connection (caller stops the run).
async function recoverOfflineId(offlineId, runCache) {
  for (const storeName of RECOVER_STORES) {
    const hit = (await getAll(storeName)).find(r => r?.clientOfflineId === offlineId && r._id && !String(r._id).startsWith('offline-'));
    if (hit) return String(hit._id);
  }
  const endpoint = SERVER_LIST_BY_PREFIX[offlineId.split('-')[1]];
  if (!endpoint) return null;
  try {
    if (!runCache[endpoint]) runCache[endpoint] = (await fetchAllWithTotal(endpoint)).rows;
  } catch (error) {
    if (!error.response && !isTimeout(error)) throw error;
    return null;
  }
  const hit = runCache[endpoint].find(r => r?.clientOfflineId === offlineId);
  return hit?._id ? String(hit._id) : null;
}

async function doPushQueue() {
  emit({ type: 'queue-start' });
  const idMap = await loadIdMap();
  const runCache = {};
  const remember = async (offlineId, realId) => { idMap[offlineId] = realId; await persistIdMap(idMap); };
  try {
    const queue = await getQueue();
    queueLoop:
    for (const item of queue) {
      // Any temporary id this change refers to that we don't know yet?
      const mentioned = [...new Set(JSON.stringify([item.url, item.data]).match(OFFLINE_ID_RE) || [])]
        .filter(id => id !== item.data?.clientOfflineId && id !== item.data?.clientRequestId
          && !id.startsWith('offline-inv-') && !id.startsWith('offline-req-') && !idMap[id]);
      for (const id of mentioned) {
        let real = null;
        try { real = await recoverOfflineId(id, runCache); }
        catch { break queueLoop; } // connection dropped — retry everything later
        if (real) { await remember(id, real); continue; }
        // Still unknown. If its create is still waiting in the queue it simply
        // hasn't gone through yet (it failed earlier) — don't send this change
        // with a temporary id; hold it until the create succeeds.
        const createPending = queue.some(q => q.id !== item.id && q.data?.clientOfflineId === id);
        await updateQueueItem(item.id, { ...item, lastError: createPending
          ? 'Waiting for the record it belongs to to be created on the server first'
          : 'The record this change belongs to could not be found on the server', failedAt: Date.now() });
        continue queueLoop;
      }
      const unresolved = OFFLINE_INV_URL.exec(item.url || '')?.[1];
      if (unresolved && !idMap[unresolved]) {
        let realId = null;
        try { realId = await resolveOfflineInventoryId(unresolved); }
        catch (error) { if (!error.response) break; }
        if (realId) await remember(unresolved, realId);
        else {
          // The record it belongs to isn't on the server (yet) — keep the edit
          // queued and retry next sync; flagged so it can't hold up pulls.
          await updateQueueItem(item.id, { ...item, lastError: 'Waiting for its inventory record to be created on the server', failedAt: Date.now() });
          continue;
        }
      }
      const url = remap(item.url, idMap);
      const data = remap(item.data, idMap);
      let res;
      try {
        const cfg = { timeout: SYNC_TIMEOUT };
        if (item.method === 'POST') res = await api.post(url, data, cfg);
        else if (item.method === 'PUT') res = await api.put(url, data, cfg);
        else if (item.method === 'DELETE') res = await api.delete(url, cfg);
      } catch (error) {
        if (isTimeout(error)) {
          // The server is reachable but didn't answer in time. Stopping here
          // (as for a dropped connection) meant one slow request blocked every
          // change queued after it — and, because it never got an error mark,
          // blocked refreshing the screens as well — for good. Keep it queued,
          // flag it so it is retried next round, and carry on with the rest.
          await updateQueueItem(item.id, { ...item, url, data, lastError: 'The server did not answer in time — will retry', failedAt: Date.now() });
          continue;
        }
        if (!error.response) break; // network dropped mid-sync — retry everything later
        // Real server error: keep this item queued (with its remapped
        // url/data persisted) for inspection/retry, but keep syncing the rest.
        await updateQueueItem(item.id, { ...item, url, data, lastError: error.response?.data?.message || error.message, failedAt: Date.now() });
        continue;
      }

      // The server call succeeded — the item is done, remove it regardless
      // of what happens in the local bookkeeping below.
      await removeQueue(item.id);

      // If this was a create carrying a clientOfflineId, learn the real id
      // and swap the temporary local record for the synced one. This is
      // local cache housekeeping only — the server already has the real
      // data, so a failure here must NEVER be treated like a network error
      // and must NEVER stop the rest of the queue from syncing.
      try {
        const offlineId = data?.clientOfflineId;
        if (item.method === 'POST' && offlineId) {
          if (url === '/inventory/stock-in') {
            // The response here is the stock movement (InventoryTransaction),
            // not the InventorySummary row whose offline id actually needs
            // remapping — its real id is never returned directly by this
            // endpoint. Look the summary up by medicine+batch instead (it
            // was just upserted, so it's the most recently updated row and
            // sorts first) so any edit already queued against this offline
            // id gets redirected to the real one instead of 404-ing forever.
            const medicineId = String(data?.medicineId || '');
            const batchNumber = String(data?.batchNumber || '');
            const invRes = await api.get('/inventory', { params: { page: 1, limit: 100 }, timeout: SYNC_TIMEOUT });
            const realSummary = (invRes?.data?.data || []).find(r =>
              String(r.medicine?._id || r.medicine) === medicineId && String(r.batchNumber || '') === batchNumber
            );
            if (realSummary?._id) {
              await remember(offlineId, String(realSummary._id));
              await remove(STORES.inventory, offlineId);
              await saveInventoryRows([realSummary]);
            }
          } else {
            const real = res?.data?.data || res?.data;
            const realId = real?._id || real?.id;
            if (realId) {
              await remember(offlineId, String(realId));
              await removeOfflineRowFromCaches(offlineId);
              const storeName = CREATE_STORE_BY_URL[url];
              if (storeName) {
                await remove(storeName, offlineId);
                await put(storeName, { ...real, _id: String(realId), syncStatus: 'synced', offline: false });
              } else if (url === '/medicines') {
                await removeMedicineCacheByMedicineId(offlineId);
                await saveMedicineResults([real]);
              }
            }
          }
        }
      } catch (localError) {
        // Local cache is now slightly stale until the next reference-data
        // refresh — harmless. Log it, but keep going.
        console.warn('sync: local cache update failed for', url, localError);
      }
    }
  } finally {
    emit({ type: 'queue-end' });
  }
}

export async function startOfflineSync() {
  if (!navigator.onLine) return;
  // Do not delay React rendering. Sync after the first paint.
  // syncQueue() = push the queue, then pull reference data.
  setTimeout(() => {
    syncQueue().catch(() => {});
  }, 300);
}

export function installOnlineListeners() {
  const onOnline = () => {
    syncQueue().catch(() => {});
  };
  window.addEventListener('online', onOnline);
  // The browser's "online" event only fires on an offline→online change. If
  // the device never reported being offline (weak/unstable connection, server
  // briefly unreachable) the queued changes were never retried until someone
  // pressed the button — so retry by itself every minute while anything waits.
  const timer = setInterval(async () => {
    try { if (navigator.onLine && (await getQueue()).length) await syncQueue(); } catch { /* retry next tick */ }
  }, 60000);
  return () => { window.removeEventListener('online', onOnline); clearInterval(timer); };
}

export async function getLastSync() { return getMeta('lastReferenceSync'); }
